import Head from "next/head";
import { Box, type SxProps, type Theme } from "@mui/material";
import type { ComponentPropsWithRef } from "react";
import { BLANK_IMAGE, useAfterPageLoadImage } from "./deferredMedia";
import {
  siteImageEntry,
  siteImageSrcSet,
  siteImageUrl,
  type SiteImageName,
} from "./siteMedia";

export type SiteImagePlaceholder = "blur" | "color" | "none";

type ImgAttributes = Omit<
  ComponentPropsWithRef<"img">,
  "src" | "srcSet" | "width" | "height" | "sizes" | "alt"
>;

export interface SiteImageProps extends ImgAttributes {
  name: SiteImageName;
  /** 화면에 보이는 폭(시안 `sizes` 그대로) — 브라우저가 이 폭 × 픽셀 비율에 맞는 너비 변형 하나만 받는다 */
  sizes: string;
  /** 장식이면 빈 문자열 */
  alt: string;
  /** 첫 화면 그림: 높은 우선순위로 바로 받고 미리 받기 링크를 단다. 아니면 페이지 load 뒤 화면 가까이 와야 받는다 */
  priority?: boolean;
  /** 받는 동안 바탕 — 흐린 미리보기(manifest 에 있을 때)·대표 색·없음(투명 그림) */
  placeholder?: SiteImagePlaceholder;
  sx?: SxProps<Theme>;
}

/**
 * 사이트 사진(site-media-delivery): 해시 이름 WebP 너비 변형 + 원본 비율의 width/height(레이아웃 흔들림 0).
 * 래퍼 없이 `<img>` 하나만 그려, 부모의 `> img` 선택자(등장 효과·채우기)가 그대로 걸린다. 첫 화면 밖 사진은
 * 주소를 페이지 load 뒤에 달아(`useAfterPageLoadImage`) 첫 화면이 대역을 먼저 쓴다.
 */
export function SiteImage({
  name,
  sizes,
  alt,
  priority = false,
  placeholder = "color",
  sx,
  ref,
  ...imgProps
}: SiteImageProps) {
  const entry = siteImageEntry(name);
  const largest = entry.w[entry.w.length - 1];
  const srcSet = siteImageSrcSet(name);
  const src = siteImageUrl(name, largest);
  const imageRef = useAfterPageLoadImage(ref, src, srcSet, !priority);
  const placeholderSx = (() => {
    if (placeholder === "none") return undefined;
    if (placeholder === "blur" && entry.q) {
      return {
        backgroundColor: entry.c,
        backgroundImage: `url(${entry.q})`,
        backgroundSize: "cover",
      };
    }
    return { backgroundColor: entry.c };
  })();
  return (
    <>
      {priority && (
        <Head>
          <link
            key={`preload-${name}`}
            rel="preload"
            as="image"
            imageSrcSet={srcSet}
            imageSizes={sizes}
            fetchPriority="high"
          />
        </Head>
      )}
      <Box
        component="img"
        ref={imageRef}
        src={priority ? src : BLANK_IMAGE}
        srcSet={priority ? srcSet : undefined}
        sizes={sizes}
        width={largest}
        height={Math.round(largest / entry.r)}
        alt={alt}
        decoding="async"
        loading={priority ? undefined : "lazy"}
        fetchPriority={priority ? "high" : undefined}
        sx={[placeholderSx ?? {}, ...(Array.isArray(sx) ? sx : [sx])]}
        {...imgProps}
      />
    </>
  );
}
